import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { formatAddress } from "encamp-shared/src/utils/address";
import {
  type CohortMembersTableQuery,
  type CohortMembersTableQueryVariables,
  CohortMemberStatus,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { prettyPrintDateTime } from "../../../../util/dates";
import { CohortMemberStatusChip } from "../../Fulfillment/FulfillmentDetails/Activities/CohortStatusChip";
import { COHORT_MEMBERS_TABLE_QUERY } from "../api";
import { BulkActionsMenu } from "../BulkActionsMenu";

export type CohortMemberRow = CohortMembersTableQuery["cohortMembers"]["items"][number];

type MembersProps = {
  reportingYear?: number;
  isClosed?: boolean;
};

export function Members({ reportingYear, isClosed }: MembersProps) {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<CohortMemberRow[]>([]);

  // For closed cohorts, default to showing only READY members
  const initialFilters = useMemo(() => {
    if (isClosed) {
      return [{ field: "status", value: CohortMemberStatus.Ready }];
    }
    return undefined;
  }, [isClosed]);

  const columns: OmnisearchGridColDef<CohortMemberRow>[] = useMemo(
    () => [
      {
        headerName: "Facility",
        field: "facilityName",
        filterKeyType: "facility",
        flex: 1.5,
        sortable: false,
        valueGetter: (_, row) => row.facility?.name ?? "",
        renderCell({ row }) {
          return (
            <Box
              component="span"
              sx={{
                cursor: "pointer",
                color: "inherit",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {row.facility?.name ?? ""}
            </Box>
          );
        },
      },
      {
        headerName: "Chemicals",
        field: "chemicalCount",
        flex: 0.6,
        sortable: false,
        valueGetter: (_, row) => row.chemicalCount ?? 0,
        renderCell({ value }) {
          return value ?? 0;
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 0.8,
        filterKeyType: "enum",
        enumValues: Object.values(CohortMemberStatus),
        renderCell({ row }) {
          return <CohortMemberStatusChip status={row.status} />;
        },
      },
      {
        headerName: "Organization",
        field: "organization",
        flex: 1.2,
        valueGetter: (_, row) => row.facility?.tenant?.name ?? "",
      },
      {
        headerName: "Address",
        field: "address",
        flex: 1.5,
        filterable: false, // Synthetic column - not filterable
        sortable: false, // Synthetic column - no backend sorting support
        valueGetter: (_, row) => {
          const addr1 = row.facility?.streetAddress1 ?? "";
          const addr2 = row.facility?.streetAddress2 ?? "";
          const streetAddress = [addr1, addr2].filter(Boolean).join(", ");
          return formatAddress(
            streetAddress || undefined,
            row.facility?.city,
            row.facility?.state,
            row.facility?.zip
          );
        },
      },
      {
        headerName: "Street Address",
        field: "streetAddress",
        filterKeyType: "text",
        flex: 1.2,
        sortable: false, // Synthetic column - no backend sorting support
        valueGetter: (_, row) => {
          const addr1 = row.facility?.streetAddress1 ?? "";
          const addr2 = row.facility?.streetAddress2 ?? "";
          return [addr1, addr2].filter(Boolean).join(", ");
        },
      },
      {
        headerName: "City",
        field: "city",
        filterKeyType: "text",
        flex: 1,
        valueGetter: (_, row) => row.facility?.city ?? "",
      },
      {
        headerName: "State",
        field: "state",
        filterKeyType: "text",
        flex: 0.8,
        valueGetter: (_, row) => row.facility?.state ?? "",
      },
      {
        headerName: "Created At",
        field: "createdAt",
        filterKeyType: "date",
        flex: 1.2,
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        filterKeyType: "date",
        flex: 1.2,
        renderCell({ value }) {
          return prettyPrintDateTime(value);
        },
      },
      {
        field: "actions",
        type: "actions",
        width: 60,
        getActions: (params) => {
          if (!params.row.tierIIReportId) return [];
          return [
            <Tooltip title="View Report Workflow" key="view">
              <GridActionsCellItem
                onClick={() =>
                  window.open(`/staff/fulfillment/r/${params.row.tierIIReportId}`, "_blank")
                }
                label="View Report Workflow"
                icon={<OpenInNew />}
              />
            </Tooltip>,
          ];
        },
      },
    ],
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [navigate]
  );

  if (!cohortId) {
    throw new Error("Cohort ID is required");
  }

  return (
    <Stack py={3} component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
      <Box sx={{ px: 2 }}>
        <Typography variant="h6" gutterBottom>
          Cohort Members
        </Typography>
        <OmnisearchDataGrid<
          CohortMembersTableQuery,
          CohortMembersTableQueryVariables,
          CohortMemberRow
        >
          commandButtons={[
            <BulkActionsMenu key="cohort-bulk-actions" selectedRows={selectedRows} />,
          ]}
          persistenceKey={`cohortMembers-${cohortId}`}
          isURLDriven
          rowHeight="auto"
          checkboxSelection
          columns={columns}
          dataQuery={COHORT_MEMBERS_TABLE_QUERY}
          dataQueryVariables={{ cohortId }}
          getItems={(data) => data.cohortMembers.items}
          getCount={(data) => data.cohortMembers.count}
          onSelectedRowsChanged={setSelectedRows}
          onRowClick={({ row }: { row: CohortMemberRow }) => {
            if (row.tierIIReportId) {
              navigate(`/staff/fulfillment/r/${row.tierIIReportId}`);
            } else if (row.facility?.id && reportingYear) {
              navigate(`/staff/fulfillment/f/${row.facility.id}/${reportingYear}`);
            }
          }}
          initialSortModel={[{ field: "facilityName", sort: "asc" }]}
          initialFilters={initialFilters}
          columnVisibilityModel={{
            streetAddress: false,
            city: false,
            state: false,
            createdAt: false,
            updatedAt: false,
          }}
          withPadding={false}
        />
      </Box>
    </Stack>
  );
}
